import Editor, { EditorProps, loader, OnMount } from "@monaco-editor/react";
import constrainedEditor from "constrained-editor-plugin";
import * as monaco from "monaco-editor";
import { editor as MonacoEditor, IDisposable } from "monaco-editor";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { makeStyles } from "@mui/styles";
import ITextModel = MonacoEditor.ITextModel;

// Make sure it is loaded from localhost instead of CDN
loader.config({ monaco });

const createTypeDefs = (variables: string) => {
    return `export type Variables = {
    ${variables}
};

export type Rule = {
    /**
     * Der Key gibt die IP-Adresse oder den IP-Adressbereich an, für den die Regel gilt.
     * @example dstNetwork=192.168.1.1/32,srcNetwork=10.10.10.0/28,srcPort=80,protocol=ip
     *          Dieses Beispiel beschränkt den Verkehr auf einen Quell- und Zielbereich 
     *          inklusive Port und Protokoll
     * @example dstNetwork=0.0.0.0/0,protocol=ip
     *          Dieses Beispiel betrifft sämtlichen Verkehr über das IP-Protokoll und kann
     *          als Standardregel verwendet werden.
     */
    [key: string]: Partial<{
        /**
         * Gibt die zusätzlich eingefügte Verzögerung in Millisekunden an.
         * @example "10.0ms"
         */
        delay: string;
        
        /**
         * Gibt die zusätzlich eingefügte zufällige Verzögerung in Millisekunden an.
         * @example "10.0ms"
         */
        jitter: string;
        
        /**
         * Gibt die Rate des künstlichen Paketverlusts an.
         * @example "5%"
         */
        loss: string;
        
        /**
         * Gibt die Rate der künstlichen Korruptionsrate der Pakete an.
         * @example "5%"
         */
        corrupt: string;
        
        /**
         * Gibt die maximale Bandbreite an, die für den Verkehr verwendet werden kann.
         * @example "10Mbit"
         */
        rate: string;
    }> | undefined;
}

/**
 * Repräsentiert die Regelkonfiguration für das gewählte Gerät.
 */
export type RuleConfiguration = Partial<{
    incoming: Rule;
    outgoing: Rule;
}>;

export type Utils = {
    /**
     * Erlaubt es, das Traffic-Verhalten eines Geräts zu steuern. Darunter können Regeln
     * für Paketverlust, Latenz und Bandbreite fallen. Die Regeln können abhängig von 
     * einzelnen IP-Adressen oder IP-Adressbereichen gesetzt werden.
     * Aus Gründen der Berechtigungen können nur Regeln für ausgehenden Traffic gesetzt
     * werden. Für eingehenden Traffic ist Kernel-Zugriff notwendig, der innerhalb eines
     * Containers nicht ohne Probleme gewährt werden kann.
     * 
     * @param device Der Name des Geräts, dessen Traffic-Verhalten gesteuert werden soll.
     *               Im Regelfall heißt dieses Gerät "eth0".
     * @return Ein Objekt, mit dem die Regeln gesetzt, gelesen und gelöscht werden können.
     */
    tc: (device: string) => {
        /**
         * Liest die aktuellen Regeln des Geräts. Dies gilt sowohl für eingehende als auch
         * ausgehende Regeln.
         * 
         * @return {Promise<RuleConfiguration>} Ein Promise, das die aktuellen Regeln des 
         *         Geräts zurückgibt.
         */
        get: () => Promise<RuleConfiguration>;
        
        /**
         * Setzt die Regeln für das Gerät. Dies gilt für ausgehenden und eingehenden Traffic.
         * Die set()-Methode ruft vor dem Setzen der Regeln automatisch die del()-Methode auf.
         * 
         * @param rules Die Regeln, die gesetzt werden sollen.
         * @return {Promise<void>} Ein Promise, das angibt, ob die Regeln erfolgreich gesetzt
         *         wurden.
         */
        set: (rules: RuleConfiguration) => Promise<void>;
        
        /**
         * Löscht die Regeln für das Gerät. Dies gilt ausschließlich für ausgehenden Traffic.
         * 
         * @return {Promise<void>} Ein Promise, das angibt, ob die Regeln erfolgreich gelöscht
         *         wurden.
         */
        del: () => Promise<void>;
    }
};`;
};

export interface CodeEditorVariable {
    defaultValue?: string;
    name: string;
    required: boolean;
    type: string;
}

export interface CodeEditorProps extends EditorProps {
    prefix: string;
    suffix: string;
    onContentChanged: (value: string) => void;
    availableVariables: CodeEditorVariable[];
}

const useStyles = makeStyles({
    nonEditable: {
        backgroundColor: "rgba(0, 0, 0, 0.05)"
    }
});

type ConstrainedEditorModelRange = {
    range: monaco.Range;
};
type ConstrainedEditorModel = ITextModel & {
    getCurrentEditableRanges: () => { editable: ConstrainedEditorModelRange };
    onDidChangeContentInEditableRange: (listener: (values: { editable: string }) => void) => void;
};

let timeout: NodeJS.Timeout | null = null;

const CodeEditorView: React.FC<CodeEditorProps> = props => {
    const classes = useStyles();

    const { onContentChanged, prefix, suffix, value, onMount, availableVariables, ...otherProps } = props;

    const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor>();
    const monacoRef = useRef<typeof monaco>();
    const loadtestModelRef = useRef<ITextModel>();

    const decoratorIdsRef = useRef<string[]>([]);
    const paddingIdsRef = useRef<string[]>([]);
    const variableTypeIdRef = useRef<IDisposable>();

    const defaultValue = (value || "    // Skript hier eingeben");

    const updateRangeStyles = useCallback((model: ConstrainedEditorModel | null) => {
        const ranges = model?.getCurrentEditableRanges();
        const range = ranges?.editable.range;
        if (range) {
            decoratorIdsRef.current = model?.deltaDecorations(decoratorIdsRef.current, [{
                range: {
                    startColumn: 0,
                    startLineNumber: 0,
                    endLineNumber: range.startLineNumber - 1,
                    endColumn: model?.getLineLength(range.startLineNumber - 1) ?? 0
                },
                options: {
                    className: classes.nonEditable,
                    isWholeLine: true
                }
            }, {
                range: {
                    startColumn: 0,
                    startLineNumber: range.endLineNumber + 1,
                    endLineNumber: model?.getLineCount() ?? 0,
                    endColumn: model?.getLineLength(model?.getLineCount() ?? 0) ?? 0
                },
                options: {
                    className: classes.nonEditable,
                    isWholeLine: true
                }
            }]) ?? [];

            // Add paddings around function block
            editorRef.current?.changeViewZones(changeAccessor => {
                paddingIdsRef.current.forEach(id => changeAccessor.removeZone(id));
                paddingIdsRef.current = [];

                const domNode1 = document.createElement("div");
                const domNode2 = document.createElement("div");
                const domNode3 = document.createElement("div");
                const domNode4 = document.createElement("div");

                domNode1.className = classes.nonEditable;
                domNode4.className = classes.nonEditable;

                paddingIdsRef.current.push(changeAccessor.addZone({
                    afterLineNumber: range.startLineNumber - 1,
                    heightInPx: 8,
                    domNode: domNode1,
                }));
                paddingIdsRef.current.push(changeAccessor.addZone({
                    afterLineNumber: range.startLineNumber - 1,
                    heightInPx: 8,
                    domNode: domNode2,
                }));
                paddingIdsRef.current.push(changeAccessor.addZone({
                    afterLineNumber: range.endLineNumber,
                    heightInPx: 8,
                    domNode: domNode3,
                }));
                paddingIdsRef.current.push(changeAccessor.addZone({
                    afterLineNumber: range.endLineNumber,
                    heightInPx: 8,
                    domNode: domNode4,
                }));
            });
        }
    }, [classes.nonEditable]);

    const updateVariableTypes = useCallback((variables: CodeEditorVariable[]) => {
        if (variableTypeIdRef.current) {
            variableTypeIdRef.current.dispose();
        }

        const available = variables.map(v => {
            switch (v.type) {
                case "BOOLEAN":
                    return v.name + ": boolean;";
                case "NUMBER":
                    return v.name + ": number" + (v.required ? "" : " | undefined") + ");";
                case "SECRET":
                case "STRING":
                    return v.name + ": string" + (v.required ? "" : " | undefined") + ");";
                default:
                    return "";
            }
        }).join("\n\t");

        // Add variable types to editor
        const uri = "file:///node_modules/utils.d.ts";
        const content = createTypeDefs(available);

        variableTypeIdRef.current = monacoRef.current?.languages.typescript.typescriptDefaults.addExtraLib(content, uri);
        if (loadtestModelRef.current) {
            loadtestModelRef.current.setValue(content);
        } else {
            loadtestModelRef.current = monacoRef.current?.editor.createModel(content, "typescript", monaco.Uri.parse(uri));
        }
    }, []);

    useEffect(() => {
        return () => {
            monacoRef.current?.editor.getModels().forEach(model => model.dispose());
        };
    }, []);

    useEffect(() => {
        timeout && clearTimeout(timeout);
        timeout = setTimeout(() => updateVariableTypes(availableVariables), 100);
    }, [availableVariables, updateVariableTypes]);

    const handleEditorDidMount: OnMount = useCallback((editor, monacoInstance) => {
        editorRef.current = editor;
        monacoRef.current = monacoInstance;

        // Create constrained editor instance and type model
        const constrainedInstance = constrainedEditor(monacoInstance);
        constrainedInstance.initializeIn(editor);
        const model = editor.getModel() as ConstrainedEditorModel;

        // Calculate lines for each section
        const prefixLines = prefix.split("\n").length - 1;
        const valueLines = defaultValue.split("\n").length;

        // Add editing constraint
        const constraints = [{
            range: [
                // Start in the line after the prefix
                prefixLines + 1,
                // At position 1
                1,
                // End in the last line of the value
                prefixLines + valueLines,
                // At the last position (add 1 so the last character is editable)
                model.getLineLength(prefixLines + valueLines) + 1
            ],
            label: "editable",
            allowMultiline: true
        }];
        constrainedInstance.addRestrictionsTo(model, constraints);
        updateRangeStyles(model);

        // Add Playwright types to editor
        const lib = require.context("!!raw-loader!/node_modules/playwright-core/", true, /\.d\.ts$/);
        lib.keys().forEach((key: string) => {
            const contents = lib(key).default;
            const uri = `file:///node_modules/playwright-core/${key.substring(2)}`;
            monaco?.languages.typescript.typescriptDefaults.addExtraLib(contents, uri);
            monaco?.editor.createModel(contents, "typescript", monaco.Uri.parse(uri));
        });

        updateVariableTypes(availableVariables);

        // Set compiler options so TypeScript finds the types added above
        monacoInstance.languages.typescript.typescriptDefaults.setCompilerOptions({
            ...monacoInstance.languages.typescript.typescriptDefaults.getCompilerOptions(),
            typeRoots: ["file:///node_modules/"],
        });

        // Update styles when content changes
        model.onDidChangeContent(() => updateRangeStyles(model));

        // Notify parent component about content changes
        model.onDidChangeContentInEditableRange(values => {
            onContentChanged(values.editable);
        });

        // Call passed onMount callback if defined
        onMount?.(editor, monacoInstance);
    }, [availableVariables, defaultValue, onContentChanged, onMount, prefix, updateRangeStyles, updateVariableTypes]);

    const fullValue = useMemo(() => {
        return prefix + defaultValue + suffix;
    }, [defaultValue, prefix, suffix]);

    return (
        <Editor
            {...otherProps}
            language="typescript"
            defaultValue={fullValue}
            onMount={handleEditorDidMount} />
    );
};

export default CodeEditorView;
